// Recepción de rutas (docs/111, pedido de Rodrigo 2026-10-25). Tesorería recibe los valores de cada ODD
// rendida: cuenta el efectivo A CIEGAS (lo declarado se ve después de contar), marca cada cheque que
// llegó y cada transferencia que vio en el banco, y con un clic se imputan todos los cobros a la
// cuenta de cada cliente. La deuda baja AQUÍ. Lo que no cuadra queda como faltante/sobrante a cargo
// del recaudador, para resolver. Las transferencias no vistas quedan «por verificar».
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, nuevaClaveIdempotencia, pedirArchivo } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';

type Medio = 'EFECTIVO' | 'CHEQUE' | 'TRANSFERENCIA';
interface OdPorRecibir {
  odId: string;
  numero: number;
  nombre: string | null;
  fechaNegocio: string;
  rendidaEn: string | null;
  patente: string | null;
  chofer: string | null;
  recaudador: string | null;
  cobros: number;
  notasDeContado: number;
  efectivo: string;
  cheques: string;
  transferencias: string;
}
interface Cobro {
  cobroId: string;
  medio: Medio;
  monto: string;
  referencia: string | null;
  banco: string | null;
  numeroDocumento: string | null;
  fechaDocumento: string | null;
  origen: 'APP' | 'MANUAL';
  anulado: boolean;
  tieneFoto: boolean;
  estadoTesoreria: string | null;
  reciboId: string | null;
}
interface Parada {
  notaId: string;
  parada: number;
  numeroVisible: string;
  cliente: string;
  estado: string;
  aCobrar: { modo: 'CONTADO' | 'COBRANZA' | 'CREDITO'; total: string };
  cobros: Cobro[];
}
interface Liquidacion {
  odId: string;
  numero: number;
  nombre: string | null;
  version: number;
  fechaNegocio: string;
  patente: string | null;
  chofer: string | null;
  recaudador: string | null;
  paradas: Parada[];
}
interface Resultado {
  imputados: Array<{ cobroId: string; reciboId: string }>;
  errores: Array<{ cobroId: string; codigo: string; mensaje: string }>;
  turno: { tipo: string; monto: string } | { error: string } | null;
  faltantes: number;
  diferenciaEfectivo: string;
}
interface Transferencia {
  cobroId: string;
  odId: string;
  odNumero: number;
  notaNumero: string;
  cliente: string;
  monto: string;
  referencia: string | null;
  tieneFoto: boolean;
  registradoEn: string;
}
interface Faltante {
  faltanteId: string;
  odNumero: number;
  tipo: 'FALTANTE' | 'SOBRANTE';
  medio: Medio;
  monto: string;
  detalle: string;
  recaudador: string | null;
  creadoEn: string;
  resolucion: string | null;
  resueltoEn: string | null;
}

const NOMBRE_MEDIO: Record<Medio, string> = {
  EFECTIVO: 'Efectivo',
  CHEQUE: 'Cheque',
  TRANSFERENCIA: 'Transferencia',
};
const pendiente = (c: Cobro) => !c.anulado && !c.reciboId && !c.estadoTesoreria;
const soloDigitos = (t: string) => t.replace(/\D/g, '');

async function abrirFoto(empresaId: string, odId: string, cobroId: string) {
  const b = await pedirArchivo('GET', `/ruta/ordenes/${odId}/cobros/${cobroId}/foto`, {
    query: { empresaId },
  });
  window.open(URL.createObjectURL(b), '_blank', 'noopener');
}

export function RecepcionRutas() {
  const [pestana, setPestana] = useState<'recibir' | 'transferencias' | 'faltantes'>('recibir');
  return (
    <div className="pantalla consulta recepcion-rutas" data-pantalla="recepcion-rutas">
      <h2>Recepción de rutas</h2>
      <p className="apagado">
        Lo que el peoneta cobró en la ruta se abona al cliente cuando tesorería lo recibe aquí.
      </p>
      <div className="acciones-fila" role="tablist">
        {(
          [
            ['recibir', 'ODD por recibir'],
            ['transferencias', 'Transferencias por verificar'],
            ['faltantes', 'Faltantes y sobrantes'],
          ] as const
        ).map(([v, t]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={pestana === v}
            className={pestana === v ? 'primario' : ''}
            onClick={() => setPestana(v)}
          >
            {t}
          </button>
        ))}
      </div>
      {pestana === 'recibir' ? (
        <PorRecibir />
      ) : pestana === 'transferencias' ? (
        <TransferenciasPorVerificar />
      ) : (
        <Faltantes />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ODD por recibir

function PorRecibir() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'por-recibir'],
    queryFn: () =>
      api.get<OdPorRecibir[]>('/tesoreria/rutas/por-recibir', { query: { empresaId } }),
    refetchInterval: 30_000,
  });
  const [abierta, setAbierta] = useState<string | null>(null);
  if (abierta) return <Recibir odId={abierta} alVolver={() => setAbierta(null)} />;
  return (
    <>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="ODD por recibir">
        <thead>
          <tr>
            <th>ODD</th>
            <th>Fecha</th>
            <th>Camión</th>
            <th>Recaudador</th>
            <th className="der">Cobros</th>
            <th className="der">Efectivo</th>
            <th className="der">Cheques</th>
            <th className="der">Transferencias</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((o) => (
            <tr key={o.odId} data-od={o.odId}>
              <td className="mono">
                {o.numero}
                {o.nombre ? ` · ${o.nombre}` : ''}
              </td>
              <td>{fecha(o.fechaNegocio)}</td>
              <td>{o.patente ?? '—'}</td>
              <td>{o.recaudador ?? o.chofer ?? '—'}</td>
              <td className="der">
                {o.cobros}
                {!o.cobros && o.notasDeContado > 0 && (
                  <span className="texto-aviso"> · {o.notasDeContado} de contado sin cobro</span>
                )}
              </td>
              <td className="der mono">{pesos(o.efectivo)}</td>
              <td className="der mono">{pesos(o.cheques)}</td>
              <td className="der mono">{pesos(o.transferencias)}</td>
              <td>
                <button type="button" className="primario" onClick={() => setAbierta(o.odId)}>
                  Recibir
                </button>
              </td>
            </tr>
          ))}
          {q.isSuccess && !q.data.length && (
            <tr>
              <td colSpan={9} className="vacio">
                No hay rutas por recibir.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function Recibir({ odId, alVolver }: { odId: string; alVolver: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'liquidacion', odId],
    queryFn: () =>
      api.get<Liquidacion>(`/tesoreria/rutas/${odId}/liquidacion`, { query: { empresaId } }),
  });
  const [contado, setContado] = useState('');
  const [revelado, setRevelado] = useState(false);
  const [llego, setLlego] = useState<Record<string, boolean>>({});
  const [datosCheque, setDatosCheque] = useState<
    Record<string, { banco: string; numero: string; fecha: string }>
  >({});
  const [observacion, setObservacion] = useState('');
  const [clave] = useState(() => nuevaClaveIdempotencia('recibir-valores'));
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [errorFoto, setErrorFoto] = useState<unknown>(null);
  const cobros = (q.data?.paradas ?? []).flatMap((p) =>
    p.cobros.filter(pendiente).map((c) => ({ ...c, parada: p })),
  );
  const declarado = (m: Medio) =>
    cobros.filter((c) => c.medio === m).reduce((t, c) => t + Number(c.monto), 0);
  const efectivoDeclarado = declarado('EFECTIVO');
  const diferencia = Number(contado || '0') - efectivoDeclarado;
  const cheques = cobros.filter((c) => c.medio === 'CHEQUE');
  const transferencias = cobros.filter((c) => c.medio === 'TRANSFERENCIA');
  const faltaDato = (c: (typeof cobros)[number]) => {
    const d = datosCheque[c.cobroId];
    const banco = c.banco || d?.banco;
    const numero = c.numeroDocumento || d?.numero || c.referencia;
    const dia = c.fechaDocumento || d?.fecha;
    return !!llego[c.cobroId] && (!banco || !numero || !dia);
  };
  const recibir = useMutation({
    mutationFn: () =>
      api.post<Resultado>(
        `/tesoreria/rutas/${odId}/recibir`,
        {
          empresaId,
          efectivoContado: soloDigitos(contado) || '0',
          cobros: cobros
            .filter((c) => c.medio !== 'EFECTIVO')
            .map((c) => {
              const d = datosCheque[c.cobroId];
              return {
                cobroId: c.cobroId,
                decision:
                  c.medio === 'CHEQUE'
                    ? llego[c.cobroId]
                      ? 'RECIBIDO'
                      : 'NO_LLEGO'
                    : llego[c.cobroId]
                      ? 'VERIFICADA'
                      : 'POR_VERIFICAR',
                ...(c.medio === 'CHEQUE' && d?.banco ? { banco: d.banco } : {}),
                ...(c.medio === 'CHEQUE' && d?.numero ? { numeroDocumento: d.numero } : {}),
                ...(c.medio === 'CHEQUE' && d?.fecha ? { fechaDocumento: d.fecha } : {}),
              };
            }),
          observacion: observacion.trim() || null,
        },
        { idempotencia: clave },
      ),
    onSuccess: async (r) => {
      setResultado(r);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
  });
  const reintentar = useMutation({
    mutationFn: () =>
      api.post<Pick<Resultado, 'imputados' | 'errores'>>(
        `/tesoreria/rutas/${odId}/imputar-pendientes`,
        { empresaId },
        { idempotencia: nuevaClaveIdempotencia('imputar-pendientes') },
      ),
    onSuccess: (r) => setResultado((x) => (x ? { ...x, ...r } : x)),
  });
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado">Cargando…</p>;
  const l = q.data;
  if (resultado)
    return (
      <section className="recuadro">
        <h3>ODD N° {l.numero} recibida</h3>
        <ul>
          <li>{resultado.imputados.length} cobro(s) abonados a la cuenta de los clientes.</li>
          {Number(resultado.diferenciaEfectivo) !== 0 && (
            <li>
              {Number(resultado.diferenciaEfectivo) < 0 ? 'Faltante' : 'Sobrante'} de efectivo:{' '}
              <b>{pesos(String(Math.abs(Number(resultado.diferenciaEfectivo))))}</b>, a cargo de{' '}
              {l.recaudador ?? l.chofer ?? 'sin recaudador'}.
              {resultado.turno && 'tipo' in resultado.turno
                ? ` Quedó como ${resultado.turno.tipo === 'RETIRO' ? 'retiro' : 'ingreso'} en tu caja.`
                : resultado.turno && 'error' in resultado.turno
                  ? ` No se pudo anotar en tu caja: ${resultado.turno.error}`
                  : ''}
            </li>
          )}
          {resultado.faltantes > 0 && (
            <li>{resultado.faltantes} diferencia(s) quedaron en «Faltantes y sobrantes».</li>
          )}
        </ul>
        {resultado.errores.length > 0 && (
          <div className="aviso-error">
            <p>No se pudieron abonar {resultado.errores.length} cobro(s):</p>
            <ul>
              {resultado.errores.map((e) => (
                <li key={e.cobroId}>{e.mensaje}</li>
              ))}
            </ul>
            <button
              type="button"
              disabled={reintentar.isPending}
              onClick={() => reintentar.mutate()}
            >
              Reintentar
            </button>
          </div>
        )}
        <button type="button" className="primario" onClick={alVolver}>
          Listo
        </button>
      </section>
    );
  return (
    <>
      <div className="acciones-fila">
        <button type="button" onClick={alVolver}>
          ‹ Volver
        </button>
        <b>
          ODD N° {l.numero}
          {l.nombre ? ` · ${l.nombre}` : ''}
        </b>
        <span className="apagado">
          {fecha(l.fechaNegocio)} · {l.patente ?? 'sin patente'} · recaudador{' '}
          {l.recaudador ?? l.chofer ?? '—'}
        </span>
      </div>
      <section className="recuadro">
        <h3>Liquidación del camión</h3>
        <table className="grilla compacta" aria-label="Liquidación por parada">
          <thead>
            <tr>
              <th>Parada</th>
              <th>Cliente</th>
              <th>Condición</th>
              <th className="der">Debía cobrar</th>
              <th className="der">Efectivo</th>
              <th className="der">Cheques</th>
              <th className="der">Transferencias</th>
            </tr>
          </thead>
          <tbody>
            {l.paradas.map((p) => {
              const suma = (m: Medio) =>
                p.cobros
                  .filter((c) => pendiente(c) && c.medio === m)
                  .reduce((t, c) => t + Number(c.monto), 0);
              const declaradoParada = suma('EFECTIVO') + suma('CHEQUE') + suma('TRANSFERENCIA');
              const falta = Number(p.aCobrar.total) > declaradoParada;
              return (
                <tr key={p.notaId}>
                  <td className="mono">
                    {p.parada} · {p.numeroVisible}
                  </td>
                  <td>{p.cliente}</td>
                  <td>
                    {p.aCobrar.modo === 'COBRANZA'
                      ? 'Cobranza'
                      : p.aCobrar.modo === 'CONTADO'
                        ? 'Contado'
                        : 'Crédito'}
                    {p.estado === 'RECHAZADA' && <span className="texto-aviso"> · rechazada</span>}
                  </td>
                  <td className={`der mono${falta && revelado ? ' texto-aviso' : ''}`}>
                    {pesos(p.aCobrar.total)}
                  </td>
                  <td className="der mono">{revelado ? pesos(String(suma('EFECTIVO'))) : '•••'}</td>
                  <td className="der mono">{pesos(String(suma('CHEQUE')))}</td>
                  <td className="der mono">{pesos(String(suma('TRANSFERENCIA')))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className="recuadro">
        <h3>1 · Efectivo (cuenta sin mirar lo declarado)</h3>
        <div className="acciones-fila">
          <label className="campo-linea">
            Efectivo contado{' '}
            <input
              aria-label="Efectivo contado"
              inputMode="numeric"
              className="mono der"
              value={contado}
              disabled={revelado}
              onChange={(e) => setContado(soloDigitos(e.target.value))}
            />
          </label>
          {!revelado ? (
            <button
              type="button"
              className="primario"
              disabled={contado === ''}
              onClick={() => setRevelado(true)}
            >
              Contado listo
            </button>
          ) : (
            <>
              <span>
                Declarado <b className="mono">{pesos(String(efectivoDeclarado))}</b>
              </span>
              <span className={diferencia === 0 ? 'texto-ok' : 'texto-aviso'}>
                {diferencia === 0
                  ? 'Cuadra'
                  : diferencia < 0
                    ? `Faltan ${pesos(String(-diferencia))}`
                    : `Sobran ${pesos(String(diferencia))}`}
              </span>
              <button type="button" onClick={() => setRevelado(false)}>
                Volver a contar
              </button>
            </>
          )}
        </div>
      </section>
      <section className="recuadro">
        <h3>2 · Cheques (marca los que te entregaron)</h3>
        {!cheques.length ? (
          <p className="apagado">Sin cheques en esta ruta.</p>
        ) : (
          <table className="grilla compacta" aria-label="Cheques de la ruta">
            <thead>
              <tr>
                <th>Llegó</th>
                <th>Cliente</th>
                <th>Banco</th>
                <th>N°</th>
                <th>Fecha</th>
                <th className="der">Monto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {cheques.map((c) => {
                const d = datosCheque[c.cobroId] ?? { banco: '', numero: '', fecha: '' };
                const poner = (k: keyof typeof d, v: string) =>
                  setDatosCheque((x) => ({ ...x, [c.cobroId]: { ...d, [k]: v } }));
                return (
                  <tr key={c.cobroId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Llegó el cheque de ${c.parada.cliente}`}
                        checked={!!llego[c.cobroId]}
                        onChange={(e) => setLlego((x) => ({ ...x, [c.cobroId]: e.target.checked }))}
                      />
                    </td>
                    <td>{c.parada.cliente}</td>
                    <td>
                      {c.banco ?? (
                        <input
                          aria-label="Banco"
                          value={d.banco}
                          onChange={(e) => poner('banco', e.target.value)}
                        />
                      )}
                    </td>
                    <td className="mono">
                      {c.numeroDocumento ?? (
                        <input
                          aria-label="Número"
                          value={d.numero || (c.referencia ?? '')}
                          onChange={(e) => poner('numero', e.target.value)}
                        />
                      )}
                    </td>
                    <td>
                      {c.fechaDocumento ? (
                        fecha(c.fechaDocumento)
                      ) : (
                        <input
                          type="date"
                          aria-label="Fecha del cheque"
                          value={d.fecha}
                          onChange={(e) => poner('fecha', e.target.value)}
                        />
                      )}
                    </td>
                    <td className="der mono">{pesos(c.monto)}</td>
                    <td>
                      {c.tieneFoto && (
                        <button
                          type="button"
                          onClick={() =>
                            void abrirFoto(empresaId, odId, c.cobroId).catch(setErrorFoto)
                          }
                        >
                          Foto
                        </button>
                      )}
                      {!llego[c.cobroId] && <span className="texto-aviso"> no llegó</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      <section className="recuadro">
        <h3>3 · Transferencias (marca las que ves en el banco)</h3>
        {!transferencias.length ? (
          <p className="apagado">Sin transferencias en esta ruta.</p>
        ) : (
          <table className="grilla compacta" aria-label="Transferencias de la ruta">
            <thead>
              <tr>
                <th>En el banco</th>
                <th>Cliente</th>
                <th>Operación</th>
                <th className="der">Monto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {transferencias.map((c) => (
                <tr key={c.cobroId}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Transferencia de ${c.parada.cliente} vista en el banco`}
                      checked={!!llego[c.cobroId]}
                      onChange={(e) => setLlego((x) => ({ ...x, [c.cobroId]: e.target.checked }))}
                    />
                  </td>
                  <td>{c.parada.cliente}</td>
                  <td className="mono">{c.referencia ?? '—'}</td>
                  <td className="der mono">{pesos(c.monto)}</td>
                  <td>
                    {c.tieneFoto && (
                      <button
                        type="button"
                        onClick={() =>
                          void abrirFoto(empresaId, odId, c.cobroId).catch(setErrorFoto)
                        }
                      >
                        Comprobante
                      </button>
                    )}
                    {!llego[c.cobroId] && <span className="apagado"> queda por verificar</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {!!errorFoto && <PanelError error={errorFoto} alCerrar={() => setErrorFoto(null)} />}
      <section className="recuadro">
        <label className="campo">
          <span className="etiqueta">Observación (opcional)</span>
          <input
            aria-label="Observación"
            maxLength={500}
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
          />
        </label>
        {recibir.isError && <PanelError error={recibir.error} alCerrar={() => recibir.reset()} />}
        <div className="acciones-fila">
          <span className="espaciador" />
          <button
            type="button"
            className="primario"
            disabled={!revelado || recibir.isPending || cheques.some(faltaDato)}
            onClick={() => recibir.mutate()}
          >
            {recibir.isPending
              ? 'Recibiendo…'
              : !revelado
                ? 'Primero cuenta el efectivo'
                : cheques.some(faltaDato)
                  ? 'Faltan datos de un cheque'
                  : 'Confirmar recepción y abonar'}
          </button>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------- transferencias por verificar

function TransferenciasPorVerificar() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'transferencias'],
    queryFn: () =>
      api.get<Transferencia[]>('/tesoreria/rutas/transferencias-por-verificar', {
        query: { empresaId },
      }),
  });
  const [rechazar, setRechazar] = useState<Transferencia | null>(null);
  const [motivo, setMotivo] = useState('');
  const [errorFoto, setErrorFoto] = useState<unknown>(null);
  const verificar = useMutation({
    mutationFn: (x: {
      t: Transferencia;
      estado: 'VERIFICADA' | 'RECHAZADA';
      observacion?: string;
    }) =>
      api.post(
        `/tesoreria/rutas/${x.t.odId}/cobros/${x.t.cobroId}/verificar`,
        { empresaId, estado: x.estado, observacion: x.observacion ?? null },
        { idempotencia: nuevaClaveIdempotencia('verificar-transferencia') },
      ),
    onSuccess: async () => {
      setRechazar(null);
      setMotivo('');
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
  });
  return (
    <>
      {q.isError && <PanelError error={q.error} />}
      {verificar.isError && !rechazar && (
        <PanelError error={verificar.error} alCerrar={() => verificar.reset()} />
      )}
      {!!errorFoto && <PanelError error={errorFoto} alCerrar={() => setErrorFoto(null)} />}
      <table className="grilla compacta" aria-label="Transferencias por verificar">
        <thead>
          <tr>
            <th>Registrada</th>
            <th>ODD</th>
            <th>Nota</th>
            <th>Cliente</th>
            <th>Operación</th>
            <th className="der">Monto</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((t) => (
            <tr key={t.cobroId}>
              <td>{fechaHora(t.registradoEn)}</td>
              <td className="mono">{t.odNumero}</td>
              <td className="mono">{t.notaNumero}</td>
              <td>{t.cliente}</td>
              <td className="mono">{t.referencia ?? '—'}</td>
              <td className="der mono">{pesos(t.monto)}</td>
              <td>
                {t.tieneFoto && (
                  <button
                    type="button"
                    onClick={() => void abrirFoto(empresaId, t.odId, t.cobroId).catch(setErrorFoto)}
                  >
                    Comprobante
                  </button>
                )}
                <button
                  type="button"
                  className="primario"
                  disabled={verificar.isPending}
                  onClick={() => verificar.mutate({ t, estado: 'VERIFICADA' })}
                >
                  La vi en el banco
                </button>
                <button type="button" onClick={() => setRechazar(t)}>
                  No apareció
                </button>
              </td>
            </tr>
          ))}
          {q.isSuccess && !q.data.length && (
            <tr>
              <td colSpan={7} className="vacio">
                No hay transferencias por verificar.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rechazar && (
        <Modal
          titulo="Transferencia que no apareció"
          ancho="angosto"
          alCerrar={() => setRechazar(null)}
          pie={
            <>
              <button type="button" onClick={() => setRechazar(null)}>
                Volver
              </button>
              <button
                type="button"
                className="primario"
                disabled={motivo.trim().length < 3 || verificar.isPending}
                onClick={() =>
                  verificar.mutate({ t: rechazar, estado: 'RECHAZADA', observacion: motivo.trim() })
                }
              >
                Registrar faltante
              </button>
            </>
          }
        >
          <p>
            {rechazar.cliente} · {pesos(rechazar.monto)}. Queda como faltante para resolver; la
            deuda del cliente no baja.
          </p>
          <label className="campo">
            <span className="etiqueta">Qué pasó</span>
            <input
              aria-label="Qué pasó"
              data-autofoco
              maxLength={300}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {verificar.isError && <PanelError error={verificar.error} />}
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------- faltantes y sobrantes

function Faltantes() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const [soloPendientes, setSoloPendientes] = useState(true);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'faltantes', soloPendientes],
    queryFn: () =>
      api.get<Faltante[]>('/tesoreria/rutas/faltantes', {
        query: { empresaId, pendientes: soloPendientes ? 'true' : 'false' },
      }),
  });
  const [resolver, setResolver] = useState<Faltante | null>(null);
  const [texto, setTexto] = useState('');
  const guardar = useMutation({
    mutationFn: (f: Faltante) =>
      api.post(
        `/tesoreria/rutas/faltantes/${f.faltanteId}/resolver`,
        { empresaId, resolucion: texto.trim() },
        { idempotencia: nuevaClaveIdempotencia('resolver-faltante') },
      ),
    onSuccess: async () => {
      setResolver(null);
      setTexto('');
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'tesoreria', 'rutas'] });
    },
  });
  const total = (q.data ?? [])
    .filter((f) => !f.resolucion)
    .reduce((t, f) => t + (f.tipo === 'FALTANTE' ? 1 : -1) * Number(f.monto), 0);
  return (
    <>
      <div className="acciones-fila">
        <label className="campo-linea">
          <input
            type="checkbox"
            checked={soloPendientes}
            onChange={(e) => setSoloPendientes(e.target.checked)}
          />{' '}
          Solo por resolver
        </label>
        <span className="espaciador" />
        <span className="apagado">
          Neto por resolver: <b className="mono">{pesos(String(total))}</b>
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Faltantes y sobrantes de ruta">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>ODD</th>
            <th>A cargo de</th>
            <th>Tipo</th>
            <th>Medio</th>
            <th className="der">Monto</th>
            <th>Detalle</th>
            <th>Resolución</th>
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((f) => (
            <tr key={f.faltanteId}>
              <td>{fecha(f.creadoEn)}</td>
              <td className="mono">{f.odNumero}</td>
              <td>{f.recaudador ?? '—'}</td>
              <td>
                <span className={`insignia ${f.tipo === 'FALTANTE' ? 'tono-malo' : 'tono-aviso'}`}>
                  {f.tipo === 'FALTANTE' ? 'Faltante' : 'Sobrante'}
                </span>
              </td>
              <td>{NOMBRE_MEDIO[f.medio]}</td>
              <td className="der mono">{pesos(f.monto)}</td>
              <td>{f.detalle}</td>
              <td>
                {f.resolucion ? (
                  <>
                    {f.resolucion} <span className="apagado">{fecha(f.resueltoEn)}</span>
                  </>
                ) : puede('tesoreria.faltante.resolver') ? (
                  <button type="button" onClick={() => setResolver(f)}>
                    Resolver
                  </button>
                ) : (
                  <span className="apagado">pendiente</span>
                )}
              </td>
            </tr>
          ))}
          {q.isSuccess && !q.data.length && (
            <tr>
              <td colSpan={8} className="vacio">
                No hay diferencias {soloPendientes ? 'por resolver' : ''}.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {resolver && (
        <Modal
          titulo={`Resolver ${resolver.tipo === 'FALTANTE' ? 'faltante' : 'sobrante'}`}
          ancho="angosto"
          alCerrar={() => setResolver(null)}
          pie={
            <>
              <button type="button" onClick={() => setResolver(null)}>
                Volver
              </button>
              <button
                type="button"
                className="primario"
                disabled={texto.trim().length < 3 || guardar.isPending}
                onClick={() => guardar.mutate(resolver)}
              >
                Guardar
              </button>
            </>
          }
        >
          <p>
            {resolver.detalle} · <b>{pesos(resolver.monto)}</b>
          </p>
          <label className="campo">
            <span className="etiqueta">Cómo se resolvió</span>
            <input
              aria-label="Cómo se resolvió"
              data-autofoco
              maxLength={500}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </label>
          {guardar.isError && <PanelError error={guardar.error} />}
        </Modal>
      )}
    </>
  );
}
